/**
 * Start (Heute) — Mirror der Web-Startseite (index.html, Stand 2026-06-12):
 * Hero „Frag dich schlau!", Startvideo, „So funktioniert dieser Kurs"
 * (3 Einstiege + Methode-Kreislauf), Quoteband, Modul-Überblick-Video,
 * Lern-Module, KI-Coach-Sektion. NotebookLM wurde 2026-06-11 auf Wunsch
 * des Auftraggebers KOMPLETT entfernt (wie im Web).
 */
import { useEffect, useState } from "preact/hooks";
import { useLocation } from "preact-iso";
import { activeCourseId, loadCourse } from "../data/course-store";
import type { Course, CourseModule } from "../data/types";
import { loadProgress, doneKeys, isDone, contentKey } from "../state/progress";
import { lastPosition } from "../state/course-nav";
import { isTrainer } from "../state/session";
import { assetUrl } from "../data/assets";
import { BrandHeader } from "../app/BrandHeader";
import { NavDrawer, MenuButton } from "../app/NavDrawer";
import { useBackClose } from "../ui/use-back-close";
import { Modal } from "../ui/Modal";

const INTRO_VIMEO = "https://player.vimeo.com/video/1198459343?fl=ml&fe=ec";
const MODULE_VIMEO = "https://player.vimeo.com/video/1200350582?fl=ml&fe=ec";

// Drei Einstiege (Auftraggeber-Konzept 2026-06-11): Lernen / Nachschlagen / Umsetzen.
const EINSTIEGE = [
  ["◈", "Lernen", "Arbeite den Kurs Modul für Modul durch und verstehe KI von Grund auf."],
  ["◈", "Nachschlagen", "Suche gezielt Antworten auf konkrete Fragen — im Kurs oder direkt bei der Kurs-KI."],
  ["◈", "Umsetzen", "Verwirkliche eigene Vorhaben mit KI-Hilfe — Schritt für Schritt in der Werkstatt."],
];

// Methode-Kreislauf: so arbeitest du mit der KI zusammen.
const STEPS = [
  ["1", "Thema oder Vorhaben wählen", "Entscheide, was du wissen oder erreichen willst — ein Modul, eine Frage, ein Projekt."],
  ["2", "Die richtige Frage stellen", "Formuliere einen guten Prompt. Der Kurs zeigt dir, wie du fragst, damit die KI dich versteht."],
  ["3", "Antwort prüfen", "Übernimm nichts ungeprüft: vergleichen, nachfragen, Belege fordern."],
  ["4", "Verfeinern & übernehmen", "Schärfe im Wechsel mit der KI nach, bis das Ergebnis passt — dann weiter zum nächsten Schritt."],
];

export function Today() {
  const { route } = useLocation();
  const [course, setCourse] = useState<Course | null>(null);
  const [infoMod, setInfoMod] = useState<CourseModule | null>(null);
  // Burger-Menü (Kursnavigation) — von Anfang an da, gleich nach dem Login.
  const [navOpen, setNavOpen] = useState(false);
  useBackClose(navOpen, () => setNavOpen(false));
  const courseId = activeCourseId.value;

  useEffect(() => {
    loadCourse(courseId).then(setCourse).catch(() => {});
    loadProgress(courseId).catch(() => {});
  }, []);

  const totalQuestions =
    course?.modules.reduce((a, m) => a + m.chapters.reduce((b, c) => b + (c.questionCount || 0), 0), 0) ?? 0;
  const doneQuestions = [...doneKeys.value].filter((k) => k.includes(":question:")).length;
  const pct = totalQuestions ? Math.round((doneQuestions / totalQuestions) * 100) : 0;

  const last = lastPosition();
  const modules = (course?.modules ?? []).filter((m) => m.group !== "trainer" || isTrainer.value);

  function openModule(m: { id: string; chapters: { id: string }[] }) {
    // Wie im Web: Karte öffnet die Modul-Eröffnungsseite (Intro), nicht direkt ein Kapitel.
    route(`/kurs?m=${encodeURIComponent(m.id)}&intro=1`);
  }

  /** „Kurs starten/fortsetzen" → erste Lücke (Web: ersteLuecke, Kapitel-Ebene;
   *  der exakte Abschnitts-Resume passiert beim Kapitel-Laden im Kurs-Screen). */
  function startCourse() {
    const teil = (course?.modules ?? [])
      .filter((m) => m.group === "teilnehmer")
      .slice()
      .sort((a, b) => (a.menueNr ?? 99) - (b.menueNr ?? 99));
    for (const m of teil) {
      for (const ch of m.chapters) {
        if (!isDone(contentKey(m.id, ch.id, "chapter", "chapter"))) {
          // resume=1 → der Kurs-Screen springt auf den ersten ungelesenen Abschnitt.
          route(`/kurs?m=${encodeURIComponent(m.id)}&b=${encodeURIComponent(ch.id)}&resume=1`);
          return;
        }
      }
    }
    route("/kurs");
  }

  return (
    <div class="stack">
      {/* Burger-Menü (☰): Kursnavigation auch auf der Startseite — sticky oben,
          die ganze Zeit erreichbar, nicht erst nach „Kurs starten". */}
      <div class="course-topbar">
        <MenuButton onClick={() => setNavOpen(true)} />
      </div>
      <NavDrawer open={navOpen} onClose={() => setNavOpen(false)} course={course} />
      <BrandHeader center={false} />

      {/* Intro-Video */}
      <div class="glass intro-video">
        <div class="video-frame">
          <iframe
            src={INTRO_VIMEO}
            title="Startvideo zum Sentinus KI-Kurs"
            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
            allowFullScreen
          />
        </div>
      </div>

      {/* Hero — 1:1 zur Web-Startseite: Kurstitel „Frag dich schlau!" (Gold) */}
      <div class="glass hero-card">
        <span class="eyebrow">Sentinus · KI-Kurs</span>
        <h1 class="hero-title">Frag dich schlau!</h1>
        <p class="claim">Lernen, indem du die richtigen Fragen stellst</p>
        <p class="motto">„Die Programme ändern sich — die Fragen bleiben gleich."</p>
        <div class="hero-cta-row">
          <button class="btn hero-primary btn--block" onClick={startCourse}>
            {last || doneQuestions > 0 ? "Kurs fortsetzen" : "Kurs starten"}
          </button>
        </div>
        <div class="progress-row hero-progress">
          <div class="bar"><i style={`width:${pct}%`} /></div>
          <span>{pct}%</span>
        </div>
      </div>

      {/* So funktioniert dieser Kurs: drei Einstiege + Methode-Kreislauf */}
      <section class="home-section">
        <h2>So funktioniert dieser Kurs</h2>
        <p class="sub">Drei Wege hinein — und eine Methode, mit der du jede KI führst.</p>
        <div class="steps">
          {EINSTIEGE.map(([n, t, d]) => (
            <div key={t} class="glass step">
              <div class="n">{n}</div>
              <h4>{t}</h4>
              <p>{d}</p>
            </div>
          ))}
        </div>
        <p class="sub" style="margin-top:26px">Egal welcher Weg — so arbeitest du mit der KI zusammen:</p>
        <div class="steps">
          {STEPS.map(([n, t, d]) => (
            <div key={n} class="glass step">
              <div class="n">{n}</div>
              <h4>{t}</h4>
              <p>{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Quoteband */}
      <div class="quoteband" style={`--quoteband-bg:url('${assetUrl("band.png")}')`}>
        <p class="q">„Du lernst zwei Dinge gleichzeitig: den Inhalt — und das Fragenstellen selbst."</p>
        <p class="by">Das Herzstück dieses Kurses</p>
      </div>

      {/* Modul-Überblick-Video (Web: direkt vor der Modul-Liste) */}
      <div class="glass intro-video">
        <div class="video-frame">
          <iframe
            src={MODULE_VIMEO}
            title="Überblick über die Kurs-Module"
            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
            allowFullScreen
          />
        </div>
      </div>

      {/* Lern-Module */}
      <section class="home-section">
        <h2>Lernen — für dich als Teilnehmer</h2>
        <p class="sub">Der Kernkurs plus Spezial-Tools, Werkzeugkästen und Praxisbeispiele.</p>
        <div class="card-grid">
          {modules.map((m) => (
            <div key={m.id} class="glass mcard">
              <button type="button" class="mcard-main" onClick={() => openModule(m)}>
                <span class={`chip ${m.group === "trainer" ? "trainer" : "teilnehmer"}`}>
                  {m.group === "trainer" ? "Für Trainer" : "Teilnehmer"}
                </span>
                <h3>{m.menueNr != null ? `Modul ${m.menueNr} · ` : ""}{m.title}</h3>
                <p>
                  {m.chapters.length} Kapitel ·{" "}
                  {m.chapters.reduce((a, c) => a + (c.questionCount || 0), 0)} Fragen
                </p>
                {(() => {
                  // Modul-Fortschrittsbalken (Web-Parität: modulStand) — erledigte Fragen / gesamt.
                  const tot = m.chapters.reduce((a, c) => a + (c.questionCount || 0), 0);
                  if (!tot) return null;
                  // gegen entfernte Fragen absichern (stale localStorage-Keys): nie > tot.
                  const done = Math.min(
                    tot,
                    [...doneKeys.value].filter((k) => k.startsWith(`${m.id}:`) && k.includes(":question:")).length,
                  );
                  const mpct = Math.round((done / tot) * 100);
                  return (
                    <div class="mcard-progress" aria-label={`${done} von ${tot} Fragen erledigt`}>
                      <div class="bar small"><i style={`width:${mpct}%`} /></div>
                      <span class="mcard-progress-num">{done}/{tot}</span>
                    </div>
                  );
                })()}
              </button>
              {m.summary ? (
                <button
                  type="button"
                  class="mcard-info"
                  aria-label={`Worum geht's in ${m.title}?`}
                  title="Worum geht's in diesem Modul?"
                  onClick={() => setInfoMod(m)}
                >
                  i
                </button>
              ) : null}
            </div>
          ))}
          {!modules.length ? <p class="text-muted">Module werden geladen …</p> : null}
        </div>
      </section>

      {/* KI-Coach-Sektion */}
      <section class="home-section">
        <div class="glass home-coach">
          <h2>Was ist dein KI Coach?</h2>
          <p>
            Dein persönlicher Lernbegleiter — eine KI, die den Kursstoff kennt und dich
            durchführt. Stell ihm die Kursfragen, lass dir Kapitel frei erklären und hak Schritt
            für Schritt ab. Nach dem Login nutzt er das aktuelle Kapitel als Kontext.
          </p>
        </div>
      </section>

      <p class="home-footer">
        Sentinus KI Coach · „Lernen durch Fragen" · Die Programme ändern sich, die Prinzipien
        bleiben.
      </p>

      {/* Modul-Kurzbeschreibung als Popup (Touch-Ersatz fürs Desktop-Mouseover). */}
      {infoMod ? (
        <Modal onClose={() => setInfoMod(null)}>
          <h2>{infoMod.menueNr != null ? `Modul ${infoMod.menueNr} · ` : ""}{infoMod.title}</h2>
          <p style="text-align:left">{infoMod.summary}</p>
          <p class="text-muted" style="font-size:13px;text-align:left">
            {infoMod.chapters.length} Kapitel ·{" "}
            {infoMod.chapters.reduce((a, c) => a + (c.questionCount || 0), 0)} Fragen
          </p>
          <div class="chapter-confirm-actions">
            <button class="btn-coach" onClick={() => { const id = infoMod.id; setInfoMod(null); route(`/kurs?m=${encodeURIComponent(id)}&intro=1`); }}>
              Modul öffnen
            </button>
            <button class="btn-ghost" onClick={() => setInfoMod(null)}>Schließen</button>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
